import React from 'react';
import {
  LinearProgress,
  PaletteMode,
  PaletteOptions,
  ThemeOptions,
  createTheme,
  css,
  responsiveFontSizes,
} from '@mui/material';

// ─── Typography ────────────────────────────────────────────────────────────────
const baseTypography: ThemeOptions['typography'] = {
  fontFamily:
    "'Inter', 'SF Pro Display', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  h1: { fontWeight: 700, letterSpacing: '-0.03em' },
  h2: { fontWeight: 700, letterSpacing: '-0.025em' },
  h3: { fontWeight: 600, letterSpacing: '-0.02em' },
  h4: { fontWeight: 600, letterSpacing: '-0.015em' },
  h5: { fontWeight: 600, letterSpacing: '-0.01em' },
  h6: { fontWeight: 600, letterSpacing: '-0.005em' },
  body1: { fontWeight: 400, letterSpacing: '-0.01em' },
  body2: { fontWeight: 400, letterSpacing: '-0.005em' },
  caption: { fontWeight: 400, letterSpacing: '0' },
  button: { fontWeight: 500, letterSpacing: '-0.01em' },
};

const monoTypography = {
  fontFamily:
    "'JetBrains Mono', 'Fira Code', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace !important",
};

// ─── Palettes ──────────────────────────────────────────────────────────────────
const createLightPalette = (): PaletteOptions => ({
  mode: 'light',
  primary: {
    main: '#5B6AF0',
    light: '#818CF8',
    dark: '#4338CA',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#06B6D4',
    light: '#22D3EE',
    dark: '#0891B2',
    contrastText: '#ffffff',
  },
  error:   { main: '#EF4444', light: '#FCA5A5', dark: '#DC2626' },
  warning: { main: '#F59E0B', light: '#FCD34D', dark: '#D97706' },
  success: { main: '#10B981', light: '#6EE7B7', dark: '#059669' },
  info:    { main: '#5B6AF0', light: '#818CF8', dark: '#4338CA' },
  background: {
    default: '#F4F5F7',
    paper:   '#FFFFFF',
  },
  chip: {
    background: '#EEF2FF',
    color:      '#4F46E5',
  },
  logs: {
    background: '#0F172A',
    text: { default: '#E2E8F0', error: '#F87171', warning: '#FCD34D' },
  },
  text: {
    primary:   '#0F172A',
    secondary: '#64748B',
    disabled:  '#94A3B8',
  },
  divider: 'rgba(15,23,42,0.08)',
  action: {
    hover:    'rgba(91,106,240,0.06)',
    selected: 'rgba(91,106,240,0.10)',
    focus:    'rgba(91,106,240,0.12)',
  },
});

const createDarkPalette = (): PaletteOptions => ({
  mode: 'dark',
  primary: {
    main: '#818CF8',
    light: '#A5B4FC',
    dark: '#6366F1',
    contrastText: '#0F172A',
  },
  secondary: {
    main: '#22D3EE',
    light: '#67E8F9',
    dark: '#06B6D4',
    contrastText: '#0F172A',
  },
  error:   { main: '#F87171', light: '#FCA5A5', dark: '#EF4444' },
  warning: { main: '#FCD34D', light: '#FDE68A', dark: '#F59E0B' },
  success: { main: '#34D399', light: '#6EE7B7', dark: '#10B981' },
  info:    { main: '#818CF8', light: '#A5B4FC', dark: '#6366F1' },
  background: {
    default: '#0D1117',
    paper:   '#161B22',
  },
  chip: {
    background: '#1E2A3A',
    color:      '#818CF8',
  },
  logs: {
    background: '#090D13',
    text: { default: '#CBD5E1', error: '#F87171', warning: '#FCD34D' },
  },
  text: {
    primary:   '#F1F5F9',
    secondary: '#94A3B8',
    disabled:  '#475569',
  },
  divider: 'rgba(241,245,249,0.08)',
  action: {
    hover:    'rgba(129,140,248,0.08)',
    selected: 'rgba(129,140,248,0.12)',
    focus:    'rgba(129,140,248,0.14)',
  },
});

// ─── Button base ──────────────────────────────────────────────────────────────
const buildButtonBase = (isDark: boolean) => ({
  defaultProps: {
    disableElevation: true,
    disableRipple: false,
    color: 'primary' as const,
    variant: 'contained' as const,
    size: 'large' as const,
  },
  styleOverrides: {
    root: {
      textTransform: 'none' as const,
      fontWeight: 500,
      borderRadius: '8px',
      transition: 'all 180ms cubic-bezier(0.4,0,0.2,1)',
      lineHeight: 1,
      position: 'relative' as const,
      overflow: 'hidden' as const,
      '&:hover': { transform: 'translateY(-1px)', filter: 'brightness(1.08)' },
      '&:active': { transform: 'translateY(0)', filter: 'brightness(0.97)' },
    },
    contained: {
      boxShadow: isDark
        ? '0 1px 3px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)'
        : '0 1px 3px rgba(91,106,240,0.25), 0 1px 2px rgba(91,106,240,0.1)',
      '&:hover': {
        boxShadow: isDark
          ? '0 4px 12px rgba(0,0,0,0.5)'
          : '0 4px 12px rgba(91,106,240,0.3)',
      },
    },
    outlined: {
      borderWidth: '1.5px',
      '&:hover': { borderWidth: '1.5px' },
    },
    sizeLarge: { fontSize: 15, height: 44, padding: '0 20px' },
    sizeMedium: { fontSize: 13, height: 34, padding: '0 14px' },
    sizeSmall: { fontSize: 12, height: 28, padding: '0 10px' },
    iconSizeLarge:  { '>svg': { fontSize: '20px !important', top: -0.5, position: 'relative' as const } },
    iconSizeMedium: { '&.MuiButton-startIcon': { marginRight: 6 }, '>svg': { fontSize: '18px !important' } },
    iconSizeSmall:  { '&.MuiButton-startIcon': { marginRight: 4 }, '>svg': { fontSize: '16px !important' } },
  },
}) as const;

// ─── Theme factory ─────────────────────────────────────────────────────────────
const createCustomTheme = (mode: PaletteMode) => {
  const palette    = mode === 'dark' ? createDarkPalette() : createLightPalette();
  const isDark     = mode === 'dark';
  const buttonBase = buildButtonBase(isDark);

  let theme = createTheme({ palette, typography: baseTypography, monoTypography, spacing: 8 });

  // Derived tokens
  const cardBg      = isDark ? '#161B22' : '#FFFFFF';
  const surfaceEl1  = isDark ? '#1C2230' : '#FFFFFF';
  const cardShadow  = isDark
    ? '0 1px 3px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)'
    : '0 1px 3px rgba(15,23,42,0.08), 0 0 0 1px rgba(15,23,42,0.04)';
  const cardShadowHover = isDark
    ? '0 4px 16px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)'
    : '0 4px 16px rgba(15,23,42,0.12), 0 0 0 1px rgba(15,23,42,0.06)';
  const tableHead     = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.02)';
  const tableAlt      = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.01)';
  const tableHover    = isDark ? 'rgba(129,140,248,0.08)' : 'rgba(91,106,240,0.04)';
  const toolbarBg     = isDark ? '#0D1117' : '#0F172A';
  const accentGradient = isDark
    ? 'linear-gradient(135deg, #818CF8 0%, #22D3EE 100%)'
    : 'linear-gradient(135deg, #5B6AF0 0%, #06B6D4 100%)';
  const dialogBg      = isDark ? 'rgba(13,17,23,0.85)' : 'rgba(244,245,247,0.85)';
  const chipBg        = (theme.palette as any).chip?.background ?? (isDark ? '#1E2A3A' : '#EEF2FF');
  const chipColor     = (theme.palette as any).chip?.color ?? (isDark ? '#818CF8' : '#4F46E5');

  theme = createTheme(theme, {
    components: {
      // ── Global CSS ──────────────────────────────────────────────────────────
      MuiCssBaseline: {
        styleOverrides: css`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

          *, *::before, *::after {
            box-sizing: border-box;
          }

          html, body {
            margin: 0;
            padding: 0;
            height: 100%;
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
            background-color: ${theme.palette.background.default};
            color: ${theme.palette.text.primary};
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }

          ::selection {
            background: ${isDark ? 'rgba(129,140,248,0.35)' : 'rgba(91,106,240,0.2)'};
          }

          :focus-visible {
            outline: 2px solid ${isDark ? '#818CF8' : '#5B6AF0'};
            outline-offset: 2px;
          }

          * { scrollbar-width: thin; scrollbar-color: ${isDark ? '#2D3748 transparent' : '#CBD5E1 transparent'}; }
          *::-webkit-scrollbar { width: 6px; height: 6px; }
          *::-webkit-scrollbar-track { background: transparent; }
          *::-webkit-scrollbar-thumb { background: ${isDark ? '#2D3748' : '#CBD5E1'}; border-radius: 99px; }
          *::-webkit-scrollbar-thumb:hover { background: ${isDark ? '#4A5568' : '#94A3B8'}; }

          .RaLayout-appFrame { margin-top: 60px !important; }

          #main-content {
            background: ${theme.palette.background.default} !important;
            padding: 24px !important;
            margin-left: 240px;
            min-height: calc(100vh - 60px);
            transition: margin-left 225ms cubic-bezier(0.4, 0, 0.6, 1);
          }

          .RaShow-main, .RaCreate-main, .RaEdit-main { margin-top: 0 !important; }
          .edit-page, .create-page { height: 100%; }

          .RaCreate-main, .RaEdit-main {
            display: flex;
            width: 100%;
            align-items: center !important;
            justify-content: center !important;
            height: 100%;
            > div { padding: 32px; max-width: 820px; }
          }

          .RaList-noResults {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100vh;
          }

          .RaBulkActionsToolbar-toolbar {
            padding-left: 20px !important;
            padding-right: 15px !important;
            align-items: center !important;
            .MuiToolbar-root { min-height: 0 !important; padding: 0 !important; }
          }

          .RaList-actions {
            align-items: center !important;
            padding-left: 2px;
            .MuiToolbar-root { align-items: center !important; padding: 6px !important; }
            & > form { min-height: 0 !important; padding: 0 !important; .MuiFormControl-root { margin-top: 0 !important; } }
          }

          .RaCreate-main, .RaEdit-main {
            .MuiToolbar-root { min-height: 0 !important; padding: 16px !important; background: none !important; }
            .RaToolbar-defaultToolbar {
              display: flex;
              justify-content: flex-end !important;
              button { flex: 1; }
              .ra-delete-button { height: 44px; flex: 0.3; margin-left: 40px; }
            }
          }

          .RaDatagrid-tableWrapper td .MuiToolbar-root {
            background: none !important;
            min-height: 0 !important;
            padding: 0 !important;
            justify-content: flex-end !important;
            .MuiButtonBase-root { min-width: 0 !important; margin-left: 8px !important; }
            .MuiButton-icon, button > svg { margin: 0 !important; }
          }

          .RaEditButton-root, [class$='RaButton-root'] {
            .MuiButton-icon { margin: 0 !important; svg { top: 0 !important; } }
          }

          .RaTabbedShowLayout-content { padding: 0 !important; margin-top: 24px !important; }

          .rdl-actions {
            margin: 0 12px !important;
            button { padding: 6px !important; margin: 0 0 2px !important; display: flex; align-items: center; outline: none !important; }
          }

          /* Page-level fade-in */
          .RaShow-main, .RaList-main, .RaCreate-main, .RaEdit-main {
            animation: pageIn 200ms ease-out both;
          }
          @keyframes pageIn {
            from { opacity: 0; transform: translateY(6px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `.styles,
      },

      // ── App bar ─────────────────────────────────────────────────────────────
      MuiAppBar: {
        styleOverrides: {
          root: {
            'background':     toolbarBg,
            'color':          '#F1F5F9',
            'border':         'none',
            'borderRadius':   0,
            'boxShadow':      'none',
            'borderBottom':   `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.1)'}`,
            'height':         60,
            'backdropFilter': 'blur(12px)',
            '-webkit-backdrop-filter': 'blur(12px)',
            '& .MuiToolbar-root': { paddingLeft: '0 !important', paddingRight: '0 !important', width: '100%' },
            '.RaAppBar-menuButton': { display: 'none' },
            '#react-admin-title': { textAlign: 'center', fontWeight: 600, marginLeft: 240, color: '#F1F5F9', fontSize: 14, opacity: 0.9 },
            '[class$="RaLoadingIndicator-root"]': { position: 'relative', right: 25 },
          },
        },
      },

      // ── Paper / Card ────────────────────────────────────────────────────────
      MuiPaper: {
        styleOverrides: {
          root: {
            background:    cardBg,
            borderRadius:  12,
            boxShadow:     cardShadow,
            border:        `1px solid ${theme.palette.divider}`,
            overflow:      'hidden',
            transition:    'box-shadow 200ms ease, border-color 200ms ease',
          },
          elevation2: { boxShadow: cardShadowHover },
          elevation3: { boxShadow: cardShadowHover },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            '&:hover': { boxShadow: cardShadowHover },
          },
        },
      },

      MuiCardHeader: {
        styleOverrides: {
          root: {
            background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.015)',
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: '14px 20px',
          },
          title: { fontWeight: 600, fontSize: 14 },
        },
      },

      MuiCardContent: {
        styleOverrides: {
          root: { padding: '20px', '&:last-child': { paddingBottom: '20px' } },
        },
      },

      // ── Buttons ─────────────────────────────────────────────────────────────
      MuiButton: buttonBase,
      MuiLoadingButton: {
        ...buttonBase,
        defaultProps: {
          ...buttonBase.defaultProps,
          loadingIndicator: React.createElement(LinearProgress, { color: 'inherit' }),
        },
        styleOverrides: {
          ...buttonBase.styleOverrides,
          root: {
            ...buttonBase.styleOverrides.root,
            '.MuiLinearProgress-root': { width: '100%' },
            '.MuiLoadingButton-loadingIndicator': { width: '50%' },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: 'all 150ms ease',
            '&:hover': { background: theme.palette.action?.hover, transform: 'scale(1.05)' },
            '&:active': { transform: 'scale(0.97)' },
          },
        },
      },

      // ── Inputs ──────────────────────────────────────────────────────────────
      MuiInputLabel: {
        defaultProps: { variant: 'outlined', disableAnimation: true, shrink: true },
        styleOverrides: {
          root: {
            transform: 'none',
            fontWeight: 500,
            display: 'block',
            position: 'unset',
            lineHeight: 1,
            fontSize: 13,
            color: theme.palette.text.secondary,
            marginBottom: 6,
          },
        },
      },

      MuiTextField: { defaultProps: { color: 'primary', variant: 'outlined' } },

      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderWidth: '1.5px !important',
            borderColor: `${theme.palette.divider} !important`,
            transition: 'border-color 150ms ease',
          },
          root: {
            'borderRadius': 8,
            'height': 44,
            'fontSize': 14,
            'background': isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
            'transition': 'box-shadow 150ms ease, background 150ms ease',

            '&.MuiInputBase-multiline': { height: 'auto' },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `${isDark ? '#818CF8' : '#5B6AF0'} !important`,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${isDark ? 'rgba(129,140,248,0.2)' : 'rgba(91,106,240,0.15)'}`,
            },

            '.MuiInputAdornment-root': { color: theme.palette.text.disabled, marginRight: 4 },
            '&.Mui-focused .MuiInputAdornment-root': { color: isDark ? '#818CF8' : '#5B6AF0' },

            'input': { padding: '0 14px', fontSize: 14, height: '100%' },

            '&.MuiInputBase-sizeSmall': {
              'height': 32, 'fontSize': 13,
              'input': { padding: '0 10px', fontSize: 13, height: '100%' },
            },

            'fieldset legend': { maxWidth: '0.01px !important' },
            '&.Mui-focused .MuiSelect-iconOutlined': { color: isDark ? '#818CF8' : '#5B6AF0' },
          },
        },
      },

      MuiSelect: {
        defaultProps: { size: 'medium' },
        styleOverrides: { select: { minHeight: 0 } },
      },

      MuiFormHelperText: {
        styleOverrides: { root: { marginLeft: 0, marginTop: 5, fontSize: 12, color: theme.palette.text.secondary } },
      },

      // ── Chips ───────────────────────────────────────────────────────────────
      MuiChip: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: {
            'background':   chipBg,
            'border':       'none',
            'borderRadius': 6,
            'fontWeight':   500,
            'transition':   'all 150ms ease',
            '.MuiChip-label, .MuiChip-icon': { 'color': `${chipColor} !important`, '&.MuiChip-labelSmall': { padding: '0 10px' } },
          },
        },
      },

      // ── Table ────────────────────────────────────────────────────────────────
      MuiTable: {
        defaultProps: { size: 'medium' },
        styleOverrides: {
          root: {
            'th': {
              textTransform: 'uppercase',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              color: theme.palette.text.secondary,
              backgroundColor: `${tableHead} !important`,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            'th, td': { border: 'none', padding: '10px 16px' },
            'tbody tr:nth-of-type(even)': { backgroundColor: tableAlt },
            'tr.MuiTableRow-hover:hover': { backgroundColor: `${tableHover} !important` },
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: { fontSize: 13 },
          head: { fontSize: 11, fontWeight: 600 },
        },
      },

      // ── Dialogs ──────────────────────────────────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            boxShadow: isDark
              ? '0 24px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.07)'
              : '0 24px 48px rgba(15,23,42,0.2), 0 0 0 1px rgba(15,23,42,0.07)',
          },
          backdrop: {
            backdropFilter: 'blur(8px)',
            '-webkit-backdrop-filter': 'blur(8px)',
            background: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(15,23,42,0.4)',
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: 16,
            fontWeight: 600,
            padding: '20px 24px 16px',
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: { root: { padding: '20px 24px' } },
      },

      MuiDialogActions: {
        styleOverrides: {
          root: {
            'display': 'flex',
            'justifyContent': 'space-between',
            'padding': '16px 24px 20px',
            'borderTop': `1px solid ${theme.palette.divider}`,
            '&:not(.custom) .MuiButton-root': {
              '&.MuiButton-containedPrimary': { color: isDark ? '#0F172A' : '#ffffff' },
              '&:first-of-type': {
                background: 'transparent',
                border: `1.5px solid ${theme.palette.divider}`,
                color: theme.palette.text.secondary,
                '&:hover': { background: theme.palette.action?.hover, borderColor: isDark ? '#818CF8' : '#5B6AF0' },
              },
              '.MuiButton-icon': { display: 'none' },
            },
          },
        },
      },

      // ── Menu / MenuItem ──────────────────────────────────────────────────────
      MuiMenuItem: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: {
            'borderRadius': 6,
            'margin': '0 4px',
            'fontSize': 13,
            'transition': 'background 120ms ease',
            '&.Mui-selected': { background: `${theme.palette.action?.selected} !important` },
            '&[role="option"]': { paddingTop: 10, paddingBottom: 10 },
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            boxShadow: isDark
              ? '0 8px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.07)'
              : '0 8px 24px rgba(15,23,42,0.15), 0 0 0 1px rgba(15,23,42,0.07)',
            padding: '4px',
          },
          list: { padding: '4px 0' },
        },
      },

      // ── Link ─────────────────────────────────────────────────────────────────
      MuiLink: {
        styleOverrides: {
          root: {
            '&, *': { color: `${isDark ? '#818CF8' : '#5B6AF0'} !important` },
            '&:hover': { textDecoration: 'underline' },
          },
        },
      },

      // ── Tabs ─────────────────────────────────────────────────────────────────
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 13,
            minHeight: 44,
            transition: 'color 150ms ease',
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: {
            background: accentGradient,
            height: 2,
            borderRadius: '2px 2px 0 0',
          },
        },
      },

      // ── Tooltip ──────────────────────────────────────────────────────────────
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            'background': isDark ? '#1C2230' : '#0F172A',
            'color':      isDark ? '#F1F5F9' : '#F8FAFC',
            'fontSize':   12,
            'fontWeight': 500,
            'borderRadius': 6,
            'padding': '5px 10px',
            'boxShadow': isDark
              ? '0 4px 12px rgba(0,0,0,0.5)'
              : '0 4px 12px rgba(15,23,42,0.25)',
            '&.isImage': {
              background: cardBg,
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
            },
          },
          arrow: { color: isDark ? '#1C2230' : '#0F172A' },
        },
      },

      // ── Switch ───────────────────────────────────────────────────────────────
      MuiSwitch: {
        styleOverrides: {
          root: { padding: 0 },
          switchBase: {
            'padding': 2,
            '&.Mui-checked': {
              '& + .MuiSwitch-track': {
                background: accentGradient,
                opacity: 1,
              },
              '.MuiSwitch-thumb': { background: '#ffffff' },
            },
          },
          thumb: {
            width: 16, height: 16,
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            transition: 'transform 150ms ease',
          },
          track: {
            borderRadius: 99,
            background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.2)',
            opacity: 1,
            transition: 'background 150ms ease',
          },
        },
      },

      // ── Divider ──────────────────────────────────────────────────────────────
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: theme.palette.divider },
        },
      },

      // ── Linear progress ──────────────────────────────────────────────────────
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 99, height: 4, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)' },
          bar:  { borderRadius: 99, background: accentGradient },
        },
      },

      // ── Circular progress ────────────────────────────────────────────────────
      MuiCircularProgress: {
        styleOverrides: {
          root: { color: isDark ? '#818CF8' : '#5B6AF0' },
        },
      },

      // ── Alert ────────────────────────────────────────────────────────────────
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 10, fontSize: 13, fontWeight: 500 },
        },
      },

      // ── Badge ────────────────────────────────────────────────────────────────
      MuiBadge: {
        styleOverrides: {
          badge: { fontWeight: 600, fontSize: 10 },
        },
      },

      // ── Skeleton ─────────────────────────────────────────────────────────────
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)',
          },
        },
      },
    },
  } as Record<string, unknown>);

  return responsiveFontSizes(theme);
};

const customTheme = createCustomTheme('light');

export { createCustomTheme, customTheme };
export default createCustomTheme;
